
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Multiselect from 'vue-multiselect';
import BarChart from 'shared/components/charts/BarChart.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import FilterSelector from './components/FilterSelector.vue';

const store = useStore();
const { t } = useI18n();

const currentUser = computed(() => store.getters.getCurrentUser);
const isAdmin = computed(() => currentUser.value?.role === 'administrator');

// ── Filtros ──────────────────────────────────────────────────────
const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedAgent = ref(null); // null = todos
const selectedTeam = ref(null); // null = todos

const agentsOptions = computed(() => store.getters['agents/getAgents'] || []);
const agentOptionsWithAll = computed(() => [
  { id: null, name: t('AI_ASSISTANTS_REPORTS.FILTERS.ALL_AGENTS') },
  ...agentsOptions.value,
]);

const teamsOptions = computed(() =>
  isAdmin.value
    ? store.getters['teams/getTeams'] || []
    : store.getters['teams/getMyTeams'] || []
);
const teamOptionsWithAll = computed(() => [
  { id: null, name: t('AI_ASSISTANTS_REPORTS.FILTERS.ALL_TEAMS') },
  ...teamsOptions.value,
]);

// ── Store ────────────────────────────────────────────────────────
const isFetching = computed(
  () => store.getters['aiAssistantsReport/uiFlags'].isFetching
);
const summary = computed(() => store.getters['aiAssistantsReport/getSummary']);

const fetchData = () => {
  if (!from.value || !to.value) return;
  store.dispatch('aiAssistantsReport/fetchSummary', {
    since: from.value,
    until: to.value,
    teamId: selectedTeam.value?.id || undefined,
    agentId: selectedAgent.value?.id || undefined,
    granularity: 'day',
    businessHours: businessHours.value,
  });
};

const onDateFilterChange = ({ from: newFrom, to: newTo, businessHours: bh }) => {
  from.value = newFrom;
  to.value = newTo;
  businessHours.value = bh || false;
  fetchData();
};

watch(selectedTeam, fetchData);
watch(selectedAgent, fetchData);

onMounted(async () => {
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
  ]);
  if (!isAdmin.value) {
    selectedAgent.value =
      agentsOptions.value.find(a => a.id === currentUser.value?.id) || null;
  }
});

// ── KPIs ─────────────────────────────────────────────────────────
const botConversations = computed(
  () => summary.value?.bot_conversations?.count ?? 0
);
const leadsIncoming = computed(() => summary.value?.leads_incoming?.count ?? 0);
const autoResolvedCount = computed(
  () => summary.value?.auto_resolved?.count ?? 0
);
const humanHandoff = computed(() => summary.value?.human_handoff?.count ?? 0);
const afterHours = computed(() => summary.value?.after_hours?.count ?? 0);

const botLeadsRatio = computed(() => {
  if (!leadsIncoming.value) return '—';
  return `${Math.round((botConversations.value / leadsIncoming.value) * 100)}%`;
});
const autoResolutionRate = computed(() => {
  if (!botConversations.value) return '0%';
  return `${Math.round((autoResolvedCount.value / botConversations.value) * 100)}%`;
});
const afterHoursRate = computed(() => {
  if (!botConversations.value) return '0%';
  return `${Math.round((afterHours.value / botConversations.value) * 100)}%`;
});

const formatResponseTime = seconds => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const botResponseTime = computed(() =>
  formatResponseTime(summary.value?.bot_response_time ?? null)
);
const humanResponseTime = computed(() =>
  formatResponseTime(summary.value?.human_response_time ?? null)
);
const botMessagesPerConv = computed(
  () => summary.value?.bot_messages_per_conv ?? 0
);
const humanMessagesPerConv = computed(
  () => summary.value?.human_messages_per_conv ?? 0
);

const hasData = computed(() => botConversations.value > 0);

// ── Gráficos ─────────────────────────────────────────────────────
const dayLabel = ts =>
  new Date(ts * 1000).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
  });

const conversationsByDayData = computed(() => {
  const ts = summary.value?.conversations_timeseries || [];
  return {
    labels: ts.map(p => dayLabel(p.timestamp)),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_HANDLED'),
        data: ts.map(p => p.value),
        backgroundColor: '#10b981',
      },
    ],
  };
});

const conversationsByHourData = computed(() => {
  const hours = summary.value?.conversations_by_hour || [];
  return {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.CONVERSATIONS'),
        data: hours,
        backgroundColor: '#3b82f6',
      },
    ],
  };
});

const byTeamData = computed(() => {
  const rows = summary.value?.by_team || [];
  return {
    labels: rows.map(r => r.name),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.CONVERSATIONS'),
        data: rows.map(r => r.count),
        backgroundColor: '#8b5cf6',
      },
    ],
  };
});

const handoffByTeamData = computed(() => {
  const rows = summary.value?.handoff_by_team || [];
  return {
    labels: rows.map(r => r.name),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.DERIVATIONS'),
        data: rows.map(r => r.count),
        backgroundColor: '#f59e0b',
      },
    ],
  };
});

// Primera respuesta: bot (barra base verde) vs humanos (azul). El toggle cambia la
// dimensión humana entre por-agente y por-asistente; el bot es una única global.
const firstResponseMode = ref('agent'); // 'agent' | 'assistant'

const firstResponseChartData = computed(() => {
  const fr = summary.value?.first_response || {};
  const rows =
    firstResponseMode.value === 'assistant'
      ? fr.by_assistant || []
      : fr.by_agent || [];
  return {
    labels: [t('AI_ASSISTANTS_REPORTS.BOT'), ...rows.map(r => r.name)],
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.FIRST_RESPONSE'),
        data: [fr.bot ?? 0, ...rows.map(r => r.seconds ?? 0)],
        backgroundColor: ['#10b981', ...rows.map(() => '#3b82f6')],
      },
    ],
  };
});

// Formato compacto para el eje (1 valor por tick): 45s / 12m / 1h 20m.
const formatDurationShort = seconds => {
  if (seconds == null) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Etiqueta con el tiempo formateado sobre cada barra (más intuitivo que segundos crudos).
const firstResponseValueLabelPlugin = {
  id: 'firstResponseValueLabels',
  afterDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (!value) return;
        ctx.save();
        ctx.fillStyle = '#64748b';
        ctx.font = '600 11px Inter, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(formatResponseTime(value), bar.x, bar.y - 4);
        ctx.restore();
      });
    });
  },
};

const firstResponseChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 20 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: { label: ctx => formatResponseTime(ctx.parsed.y) },
    },
  },
  scales: {
    x: { grid: { drawOnChartArea: false } },
    y: {
      beginAtZero: true,
      grid: { drawOnChartArea: false },
      ticks: { callback: value => formatDurationShort(value) },
    },
  },
}));

// Atendidas (total) vs derivadas por asistente, alineadas por team_id.
const handledVsDerivedData = computed(() => {
  const total = summary.value?.by_team || [];
  const handoffMap = new Map(
    (summary.value?.handoff_by_team || []).map(r => [r.team_id, r.count])
  );
  return {
    labels: total.map(r => r.name),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.HANDLED'),
        data: total.map(r => r.count),
        backgroundColor: '#8b5cf6',
      },
      {
        label: t('AI_ASSISTANTS_REPORTS.DERIVATIONS'),
        data: total.map(r => handoffMap.get(r.team_id) || 0),
        backgroundColor: '#f59e0b',
      },
    ],
  };
});

const barChartOptions = (precision = 0, yTitle = null) => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      ...(yTitle ? { title: { display: true, text: yTitle } } : {}),
      ticks: { precision },
    },
  },
});

// ── Embudo AIDA ──────────────────────────────────────────────────
// Barras horizontales decrecientes: ancho = alcanzados / alcanzados-en-etapa-1.
const aidaFunnel = computed(() => summary.value?.aida_funnel || []);
const aidaFirstCount = computed(() => aidaFunnel.value[0]?.count || 0);
const aidaBarWidth = count =>
  aidaFirstCount.value ? `${(count / aidaFirstCount.value) * 100}%` : '0%';
const aidaPctOfFirst = count =>
  aidaFirstCount.value ? Math.round((count / aidaFirstCount.value) * 100) : 0;

// ── Estado del Lead (barra descendente con % por estado) ─────────
const leadStatusData = computed(() => {
  const rows = summary.value?.lead_status_distribution || [];
  return {
    labels: rows.map(r => r.status),
    datasets: [
      {
        label: t('AI_ASSISTANTS_REPORTS.CONVERSATIONS'),
        data: rows.map(r => r.count),
        backgroundColor: '#6366f1',
      },
    ],
  };
});

// Etiqueta sobre cada barra: "cantidad (porcentaje%)".
const leadValueLabelPlugin = {
  id: 'leadValueLabels',
  afterDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    if (!dataset) return;
    const total = dataset.data.reduce((a, b) => a + b, 0);
    chart.getDatasetMeta(0).data.forEach((bar, index) => {
      const value = dataset.data[index];
      if (!value) return;
      const pct = total ? Math.round((value / total) * 100) : 0;
      ctx.save();
      ctx.fillStyle = '#64748b';
      ctx.font = '600 11px Inter, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`${value} (${pct}%)`, bar.x, bar.y - 4);
      ctx.restore();
    });
  },
};

const leadStatusChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 20 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ctx => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((ctx.parsed.y / total) * 100) : 0;
          return `${ctx.parsed.y} (${pct}%)`;
        },
      },
    },
  },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};
</script>

<template>
  <div class="flex flex-col h-full">
    <ReportHeader
      :header-title="$t('AI_ASSISTANTS_REPORTS.HEADER')"
      :header-description="$t('AI_ASSISTANTS_REPORTS.DESCRIPTION')"
    />

    <!-- Filtros -->
    <div class="flex flex-col gap-3 px-8 pt-4 pb-2">
      <FilterSelector
        :show-agents-filter="false"
        :show-group-by-filter="false"
        :show-business-hours-switch="true"
        @filter-change="onDateFilterChange"
      />
      <div class="flex items-center gap-3 flex-wrap">
        <div class="multiselect-wrap--small">
          <Multiselect
            v-model="selectedTeam"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AI_ASSISTANTS_REPORTS.FILTERS.ALL_TEAMS')"
            :options="teamOptionsWithAll"
            :searchable="true"
            :allow-empty="false"
            :show-labels="false"
          />
        </div>
        <div class="multiselect-wrap--small">
          <Multiselect
            v-model="selectedAgent"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AI_ASSISTANTS_REPORTS.FILTERS.ALL_AGENTS')"
            :options="
              isAdmin
                ? agentOptionsWithAll
                : agentOptionsWithAll.filter(a => a.id === currentUser?.id)
            "
            :searchable="isAdmin"
            :allow-empty="false"
            :show-labels="false"
            :disabled="!isAdmin"
          />
        </div>
      </div>
    </div>

    <div class="flex-1 px-8 pb-8 overflow-auto">
      <div class="space-y-6">
        <!-- Fila 1 -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_HANDLED')"
            :value="botConversations.toString()"
            :info-text="$t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_HANDLED_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.LEADS_INCOMING')"
            :value="leadsIncoming.toString()"
            :info-text="$t('AI_ASSISTANTS_REPORTS.LEADS_INCOMING_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.BOT_LEADS_RATIO')"
            :value="botLeadsRatio"
            :info-text="$t('AI_ASSISTANTS_REPORTS.BOT_LEADS_RATIO_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.AUTO_RESOLUTION_RATE')"
            :value="autoResolutionRate"
            :info-text="$t('AI_ASSISTANTS_REPORTS.AUTO_RESOLUTION_RATE_INFO')"
          />
        </div>

        <!-- Fila 2 -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.BOT_AVG_RESPONSE')"
            :value="botResponseTime"
            :info-text="$t('AI_ASSISTANTS_REPORTS.BOT_AVG_RESPONSE_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.HUMAN_AVG_RESPONSE')"
            :value="humanResponseTime"
            :info-text="$t('AI_ASSISTANTS_REPORTS.HUMAN_AVG_RESPONSE_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.BOT_MESSAGES_PER_CONV')"
            :value="botMessagesPerConv.toString()"
            :info-text="$t('AI_ASSISTANTS_REPORTS.BOT_MESSAGES_PER_CONV_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.HUMAN_MESSAGES_PER_CONV')"
            :value="humanMessagesPerConv.toString()"
            :info-text="$t('AI_ASSISTANTS_REPORTS.HUMAN_MESSAGES_PER_CONV_INFO')"
          />
        </div>

        <!-- Fila 3 -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.HUMAN_HANDOFF')"
            :value="humanHandoff.toString()"
            :info-text="$t('AI_ASSISTANTS_REPORTS.HUMAN_HANDOFF_INFO')"
          />
          <ReportMetricCard
            :label="$t('AI_ASSISTANTS_REPORTS.AVAILABILITY_24_7')"
            :value="afterHoursRate"
            :info-text="$t('AI_ASSISTANTS_REPORTS.AVAILABILITY_24_7_INFO')"
          />
        </div>

        <!-- Chart: por día -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_PER_DAY') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_PER_DAY_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="conversationsByDayData"
              :chart-options="barChartOptions(0)"
            />
          </div>
        </div>

        <!-- Chart: por equipo / asistente -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.BY_TEAM') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.BY_TEAM_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="byTeamData"
              :chart-options="barChartOptions(0)"
            />
          </div>
        </div>

        <!-- Chart: primera respuesta — bot base vs humanos (toggle agente/asistente) -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h3
              class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.FIRST_RESPONSE_COMPARISON') }}
              <fluent-icon
                v-tooltip="$t('AI_ASSISTANTS_REPORTS.FIRST_RESPONSE_COMPARISON_INFO')"
                size="16"
                icon="info"
                class="text-slate-400"
              />
            </h3>
            <div
              class="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-sm"
            >
              <button
                type="button"
                class="px-3 py-1.5"
                :class="
                  firstResponseMode === 'agent'
                    ? 'bg-woot-500 text-white'
                    : 'text-slate-600 dark:text-slate-300'
                "
                @click="firstResponseMode = 'agent'"
              >
                {{ $t('AI_ASSISTANTS_REPORTS.BY_AGENT') }}
              </button>
              <button
                type="button"
                class="px-3 py-1.5"
                :class="
                  firstResponseMode === 'assistant'
                    ? 'bg-woot-500 text-white'
                    : 'text-slate-600 dark:text-slate-300'
                "
                @click="firstResponseMode = 'assistant'"
              >
                {{ $t('AI_ASSISTANTS_REPORTS.BY_ASSISTANT') }}
              </button>
            </div>
          </div>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="firstResponseChartData"
              :chart-options="firstResponseChartOptions"
              :plugins="[firstResponseValueLabelPlugin]"
            />
          </div>
        </div>

        <!-- Chart: derivaciones por asistente -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.HANDOFF_BY_TEAM') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.HANDOFF_BY_TEAM_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="handoffByTeamData"
              :chart-options="barChartOptions(0)"
            />
          </div>
        </div>

        <!-- Chart: atendidas vs derivadas por asistente -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.HANDLED_VS_DERIVED') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.HANDLED_VS_DERIVED_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="handledVsDerivedData"
              :chart-options="barChartOptions(0)"
            />
          </div>
        </div>

        <!-- Chart: por hora -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_BY_HOUR') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.CONVERSATIONS_BY_HOUR_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="conversationsByHourData"
              :chart-options="
                barChartOptions(0, $t('AI_ASSISTANTS_REPORTS.CONVERSATIONS'))
              "
            />
          </div>
        </div>

        <!-- Funnel: Estado AIDA -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.AIDA_FUNNEL') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.AIDA_FUNNEL_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div
            v-if="!isFetching && !aidaFirstCount"
            class="text-center py-12 text-sm text-slate-400"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
          </div>
          <div v-else class="space-y-3">
            <div v-for="stage in aidaFunnel" :key="stage.stage">
              <div class="flex items-center justify-between mb-1 text-sm">
                <span class="font-medium text-slate-700 dark:text-slate-200">
                  {{ stage.stage }}
                </span>
                <span class="text-slate-500 dark:text-slate-400">
                  {{ stage.count }} · {{ aidaPctOfFirst(stage.count) }}%
                </span>
              </div>
              <div
                class="h-6 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden"
              >
                <div
                  class="h-full rounded"
                  :style="{
                    width: aidaBarWidth(stage.count),
                    backgroundColor: '#10b981',
                  }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Chart: Estado del Lead (barra descendente + %) -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3
            class="flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
          >
            {{ $t('AI_ASSISTANTS_REPORTS.LEAD_STATUS') }}
            <fluent-icon
              v-tooltip="$t('AI_ASSISTANTS_REPORTS.LEAD_STATUS_INFO')"
              size="16"
              icon="info"
              class="text-slate-400"
            />
          </h3>
          <div class="h-72">
            <div
              v-if="!isFetching && !hasData"
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t('AI_ASSISTANTS_REPORTS.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="leadStatusData"
              :chart-options="leadStatusChartOptions"
              :plugins="[leadValueLabelPlugin]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
